import { useEffect, useState } from "react";
import { type ListSubmissionsParams, fromWireSubmission, getSubmission, listSubmissions } from "../api";
import type { SubmissionForm, SubmissionListResponse } from "../types/submission";

export type SubmissionQueryState = { status: "loading" } | { status: "error"; error: Error } | { status: "ready"; data: SubmissionForm };

export function useSubmission(id: string | undefined): SubmissionQueryState {
    const [state, setState] = useState<SubmissionQueryState>({ status: "loading" });

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setState({ status: "loading" });
        getSubmission(id)
            .then((wire) => {
                if (!cancelled) setState({ status: "ready", data: fromWireSubmission(wire) });
            })
            .catch((error: Error) => {
                if (!cancelled) setState({ status: "error", error });
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    return state;
}

export type SubmissionsQueryState =
    | { status: "loading" }
    | { status: "error"; error: Error }
    | { status: "ready"; data: SubmissionListResponse };

/**
 * `enabled: false` holds the hook in `loading` without firing a request.
 * Triage uses it to wait for the user's saved filters (OR-1384) — those
 * are query params, so listing before they load would fetch the wrong
 * page and then immediately refetch.
 */
export interface UseSubmissionsOptions {
    enabled?: boolean;
}

export function useSubmissions(
    params: ListSubmissionsParams = {},
    { enabled = true }: UseSubmissionsOptions = {},
): SubmissionsQueryState {
    const [state, setState] = useState<SubmissionsQueryState>({ status: "loading" });
    const key = JSON.stringify(params);

    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading" });
        if (!enabled) return;
        listSubmissions(params)
            .then((data) => {
                if (!cancelled) setState({ status: "ready", data });
            })
            .catch((error: Error) => {
                if (!cancelled) setState({ status: "error", error });
            });
        return () => {
            cancelled = true;
        };
        // params is JSON-encoded into `key` so the effect re-runs when
        // any filter actually changes; the eslint plugin can't see
        // through the JSON.stringify so we silence it explicitly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled]);

    return state;
}
